import { useState } from 'react';

// THE REAL JOURNEY. The funnel says how many reached each stage; this says what happened
// at each one — who continued, who went straight to the psychiatrist, who stopped, and why.
// Every CM follow-up level inside every cycle is its own row, because "left after the first
// follow-up" and "left after the fourth" are different problems with different fixes
// (Ohad, 2026-10-07).
//
// It reconciles by construction: inside a cycle, each level's `exact` splits into exactly
// `went_on` + `to_psy` + `stopped`, the levels' `exact` sum to `entered`, and the levels'
// `to_psy` sum to the next psychiatrist milestone's `reached`. Verified on February 2026:
// 501 entered → 211 reached psy-fu 1 → 290 stopped, and the 290 carry 290 reasons.

type Detail = { label: string; count: number; stale: number };
type Reason = { key: string; label: string; count: number; cat: string; stale: number; detail: Detail[] };
type Step = {
  n: number; reached: number; pct: number; exact: number;
  went_on: number; to_psy: number; stopped: number; reasons: Reason[];
};
export type Stage = {
  key: string; label: string; kind: string; reached: number; pct: number;
  entered: number; avg_cm: number; in_flight: number; skip: number;
  pct_of_prev: number; steps: Step[];
};
type Current = { key: string; reasons: Reason[] };

const fmt = (n: number) => n.toLocaleString();
const p = (a: number, b: number) => (b ? Math.round((a / b) * 100) : 0);
const CAT: Record<string, string> = {
  finished: 'var(--ex-text-secondary)',
  stuck: 'var(--ex-red-bar, #EF4444)',
  scheduled: 'var(--ex-text-muted)',
};

/** One reason line, with its invitation depth and how much of it has gone quiet. */
function ReasonLine({ r, indent = 0 }: { r: Reason; indent?: number }) {
  return (
    <>
      <div style={{ display: 'flex', gap: 8, fontSize: 12.5, color: CAT[r.cat] ?? CAT.stuck,
                    paddingInlineStart: indent, alignItems: 'baseline' }}>
        <span style={{ minWidth: 38, textAlign: 'left', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{fmt(r.count)}</span>
        <span>{r.label}</span>
        {r.stale > 0 && (
          <span style={{ fontSize: 11, color: 'var(--ex-red-bar, #EF4444)', fontWeight: 700 }}>
            · {fmt(r.stale)} בלי פגישה מעל 30 יום
          </span>
        )}
      </div>
      {r.detail.map((d) => (
        <div key={d.label} style={{ display: 'flex', gap: 8, fontSize: 11.5, color: 'var(--ex-text-muted)',
                                    paddingInlineStart: indent + 46 }}>
          <span style={{ minWidth: 30, textAlign: 'left', fontVariantNumeric: 'tabular-nums' }}>{fmt(d.count)}</span>
          <span>{d.label}{d.stale > 0 ? ` · ${fmt(d.stale)} שקטים` : ''}</span>
        </div>
      ))}
    </>
  );
}

const BRANCH: Record<string, { color: string; mark: string }> = {
  on: { color: 'var(--ex-green-bar, #047857)', mark: '↓' },
  psy: { color: 'var(--ex-blue-text, #2563EB)', mark: '↗' },
  wait: { color: 'var(--ex-text-muted)', mark: '⏳' },
  stop: { color: 'var(--ex-text-secondary)', mark: '✕' },
};

const GROUPS: { cat: string; label: string }[] = [
  { cat: 'finished', label: 'נסגרו' },
  { cat: 'stuck', label: 'תקועים — פתוחים, בלי תור' },
];

/** Who stopped here, split into closed vs stuck IN WORDS. The three categories used to be
 *  distinguishable only by the colour of the reason text, which does not answer the question
 *  the panel is for: of the 499 who did not continue, how many are actually closed
 *  (Ohad, 2026-10-07). "A future appointment" is pulled out above — it is not a stop. */
function StopBlock({ reasons, of }: { reasons: Reason[]; of: number }) {
  const sched = reasons.filter((r) => r.cat === 'scheduled');
  const waiting = sched.reduce((a, r) => a + r.count, 0);
  const stopped = reasons.filter((r) => r.cat !== 'scheduled');
  const total = stopped.reduce((a, r) => a + r.count, 0);
  if (!reasons.length) return null;
  return (
    <>
      <Branch kind="wait" label="תור עתידי — טרם התקיים" n={waiting} of={of} />
      <Branch kind="stop" label="נטשו כאן" n={total} of={of} />
      <div style={{ paddingInlineStart: 14, display: 'flex', flexDirection: 'column', gap: 1, marginTop: 1 }}>
        {GROUPS.map((g) => {
          const rs = stopped.filter((r) => r.cat === g.cat);
          const n = rs.reduce((a, r) => a + r.count, 0);
          if (!n) return null;
          return (
            <div key={g.cat} style={{ marginTop: 2 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: g.cat === 'stuck' ? CAT.stuck : 'var(--ex-text-secondary)' }}>
                {g.label} · {fmt(n)} · {p(n, total)}% ממי שעצר
              </div>
              {rs.map((r) => <ReasonLine key={r.key} r={r} indent={10} />)}
            </div>
          );
        })}
      </div>
    </>
  );
}

function Branch({ kind, label, n, of }: { kind: string; label: string; n: number; of: number }) {
  if (n <= 0) return null;
  const b = BRANCH[kind];
  return (
    <div style={{ fontSize: 13, color: b.color, fontWeight: 700, padding: '1px 0' }}>
      {b.mark} {label} · {fmt(n)} · {p(n, of)}%
    </div>
  );
}

/** The CM levels inside one cycle. Level 0 = entered the cycle and did no follow-up at all. */
function CycleLevels({ st, nextLabel, start, lostBy }:
  { st: Stage; nextLabel: string; start: number; lostBy: (k: string) => number }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 6 }}>
      {st.steps.filter((s) => s.n > 0).map((s) => {
        if (s.exact <= 0 && s.reached <= 0) return null;
        const title = s.n === 4 ? 'מעקב 4 ואילך' : `מעקב ${s.n}`;
        return (
          <div key={s.n}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 13, fontWeight: 700, minWidth: 148 }}>{title}</span>
              <span style={{ fontSize: 15, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                {fmt(s.reached)} <span style={{ fontSize: 11.5, fontWeight: 400, color: 'var(--ex-text-muted)' }}>הגיעו</span>
              </span>
              <span style={{ fontSize: 11.5, color: 'var(--ex-text-muted)' }}>
                {p(s.reached, start)}% מהתחלה · נטישה מצטברת {p(lostBy(`${st.key}#${s.n}`), start)}%
              </span>
            </div>
            <div style={{ paddingInlineStart: 16, borderInlineStart: '2px solid var(--ex-border)', marginTop: 2 }}>
              <Branch kind="on" label={`המשיכו למעקב ${s.n + 1}`} n={s.went_on} of={s.reached} />
              <Branch kind="psy" label={`המשיכו ל${nextLabel}`} n={s.to_psy} of={s.reached} />
              <StopBlock reasons={s.reasons} of={s.reached} />
            </div>
          </div>
        );
      })}
    </div>
  );
}


// ── Churn ────────────────────────────────────────────────────────────────────
// Two numbers, and they answer different questions (Ohad, 2026-10-08):
//   נטישה בשלב     of those who ARRIVED here, how many stopped here
//   נטישה מצטברת   a RUNNING SUM of everyone lost up to and including this row,
//                  over the original cohort
// The cumulative column was first written as 100 − (arrived ÷ cohort), which read 89%
// at the fourth follow-up of cycle 1 and 69% at the psychiatrist visit AFTER it. The
// follow-up levels are depth inside a cycle, not rungs on one ladder: a patient who did
// two follow-ups and then saw the psychiatrist did not churn, so "did not reach level 4"
// is not a loss. The running sum is monotone and closes — on February 2026 it ends at
// 572 churned + 114 holding an appointment = 686, the cohort exactly.
// A patient holding a future appointment has NOT churned — they are on the way — so
// `scheduled_forward` is excluded from both. Counting them as churn would charge the
// process for people it has not lost.
type Row = {
  key: string; label: string; depth: number;
  arrived: number; churned: number; waiting: number;
};

function buildRows(sequence: Stage[], reasonsOf: (k: string) => Reason[]): Row[] {
  const stops = (rs: Reason[]) => ({
    churned: rs.filter((r) => r.cat !== 'scheduled').reduce((a, r) => a + r.count, 0),
    waiting: rs.filter((r) => r.cat === 'scheduled').reduce((a, r) => a + r.count, 0),
  });
  const out: Row[] = [];
  sequence.forEach((st) => {
    if (st.kind === 'milestone') {
      out.push({ key: st.key, label: st.label, depth: 0, arrived: st.reached, ...stops(reasonsOf(st.key)) });
    } else {
      st.steps.filter((x) => x.n > 0 && x.reached > 0).forEach((x) => {
        out.push({
          key: `${st.key}#${x.n}`, depth: 1,
          label: `${st.label} · ${x.n === 4 ? 'מעקב 4+' : `מעקב ${x.n}`}`,
          arrived: x.reached, ...stops(x.reasons),
        });
      });
    }
  });
  return out;
}

function ChurnTable({ rows }: { rows: Row[] }) {
  const start = rows[0]?.arrived ?? 0;
  const cell: React.CSSProperties = { padding: '3px 8px', textAlign: 'center', fontVariantNumeric: 'tabular-nums' };
  return (
    <div style={{ overflowX: 'auto', marginBottom: 12 }}>
      <table style={{ borderCollapse: 'collapse', fontSize: 12.5, minWidth: 520, width: '100%' }}>
        <thead>
          <tr style={{ color: 'var(--ex-text-muted)', fontSize: 11.5, textAlign: 'center' }}>
            <th style={{ ...cell, textAlign: 'start' }}>שלב</th>
            <th style={cell}>הגיעו</th>
            <th style={cell}>% מהתחלה</th>
            <th style={cell}>נטשו כאן</th>
            <th style={cell}>נטישה בשלב</th>
            <th style={cell}>נטישה מצטברת</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const step = p(r.churned, r.arrived);
            const lost = rows.slice(0, i + 1).reduce((a, x) => a + x.churned, 0);
            return (
              <tr key={r.key} style={{ borderTop: '1px solid var(--ex-border)' }}>
                <td style={{ ...cell, textAlign: 'start', paddingInlineStart: 8 + r.depth * 16,
                             color: r.depth ? 'var(--ex-text-secondary)' : undefined,
                             fontWeight: r.depth ? 400 : 700 }}>{r.label}</td>
                <td style={{ ...cell, fontWeight: 700 }}>{fmt(r.arrived)}</td>
                <td style={cell}>{p(r.arrived, start)}%</td>
                <td style={cell}>{fmt(r.churned)}</td>
                <td style={{ ...cell, fontWeight: 700,
                             color: step >= 30 ? 'var(--ex-red-bar, #EF4444)' : undefined }}>{step}%</td>
                <td style={{ ...cell, color: 'var(--ex-text-secondary)' }}>{p(lost, start)}%</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div style={{ fontSize: 11.5, color: 'var(--ex-text-secondary)', marginTop: 6 }}>
        <b>{fmt(rows.reduce((a, r) => a + r.churned, 0))}</b> נטשו ·
        {' '}<b>{fmt(rows.reduce((a, r) => a + r.waiting, 0))}</b> מחזיקים תור עתידי ·
        {' '}סך הכל <b>{fmt(start)}</b>
      </div>
      <div style={{ fontSize: 11, color: 'var(--ex-text-muted)', marginTop: 2 }}>
        מי שמחזיק תור עתידי אינו נטישה — הוא בדרך, ולכן אינו נספר באף אחת משתי עמודות
        הנטישה. "נטישה מצטברת" סופרת את כל מי שאבד עד השורה הזו ולמטה, מול {fmt(start)}
        שבתחילת המסע; "% מהתחלה" היא כמה מהם הגיעו הלוך עד כאן.
      </div>
    </div>
  );
}

export function JourneyFlow({ sequence, current, anchorLabel }:
  { sequence: Stage[]; current: Current[]; anchorLabel: string }) {
  // The first cycle is open; the rest are one click away. Four cycles fully expanded is
  // twenty levels, and nobody reads the fourth before they have read the first.
  const [open, setOpen] = useState<Record<string, boolean>>(
    () => Object.fromEntries(sequence.filter((s) => s.kind === 'cm_cycle').map((s, i) => [s.key, i === 0])),
  );
  const reasonsOf = (key: string) => (current.find((c) => c.key === key)?.reasons ?? []).filter((r) => !r.key.includes('@'));
  const labelAfter = (i: number) => sequence.slice(i + 1).find((s) => s.kind === 'milestone')?.label ?? 'פסיכיאטר';
  const rows = buildRows(sequence, reasonsOf);
  const start = rows[0]?.arrived ?? 0;
  /** Everyone churned from the start of the journey down to (and including) this row. */
  const lostBy = (key: string) => {
    const i = rows.findIndex((r) => r.key === key);
    return i < 0 ? 0 : rows.slice(0, i + 1).reduce((a, r) => a + r.churned, 0);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <ChurnTable rows={rows} />
      {sequence.map((st, i) => {
        const next = sequence[i + 1];
        if (st.kind === 'milestone') {
          // Who continued past this milestone. A cycle's `entered` is BY DEFINITION everyone
          // who reached the milestone before it, so reading it as "continued" always printed
          // 100% while the stop reasons sat right underneath it (Ohad, 2026-10-07). Continuing
          // past the milestone means doing at least one follow-up in the cycle, or skipping
          // the cycle for the next psychiatrist visit.
          const continued = !next ? 0
            : next.kind === 'cm_cycle' ? next.reached + (next.steps.find((x) => x.n === 0)?.to_psy ?? 0)
            : next.reached;
          const stopped = Math.max(st.reached - continued, 0);
          return (
            <div key={st.key}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 14.5, fontWeight: 700, minWidth: 150 }}>{st.label}</span>
                <span style={{ fontSize: 19, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{fmt(st.reached)}</span>
                <span style={{ fontSize: 11.5, color: 'var(--ex-text-muted)' }}>
                  {p(st.reached, start)}% מהתחלה · נטישה מצטברת {p(lostBy(st.key), start)}%
                </span>
                {st.in_flight > 0 && (
                  <span style={{ fontSize: 11.5, color: 'var(--ex-text-muted)' }}>
                    + {fmt(st.in_flight)} עם תור עתידי, בדרך לכאן
                  </span>
                )}
              </div>
              <div style={{ paddingInlineStart: 18, borderInlineStart: '2px solid var(--ex-border)', marginTop: 3 }}>
                <Branch kind="on" label={next ? `המשיכו ל${next.kind === 'cm_cycle' ? 'מעקבי מנהל-תיק' : next.label}` : 'המשיכו'}
                        n={continued} of={st.reached} />
                <StopBlock reasons={reasonsOf(st.key)} of={st.reached} />
                {stopped !== reasonsOf(st.key).reduce((a, r) => a + r.count, 0) && (
                  <div style={{ fontSize: 11, color: 'var(--ex-amber-text, #92400E)' }}>
                    ⚠ {fmt(stopped)} לא המשיכו אבל {fmt(reasonsOf(st.key).reduce((a, r) => a + r.count, 0))} סיבות — פער שצריך בדיקה
                  </div>
                )}
              </div>
            </div>
          );
        }
        const isOpen = !!open[st.key];
        return (
          <div key={st.key} style={{ border: '1px solid var(--ex-border)', borderRadius: 10, padding: '8px 10px' }}>
            <button type="button" onClick={() => setOpen((o) => ({ ...o, [st.key]: !o[st.key] }))}
              style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', cursor: 'pointer',
                       color: 'inherit', display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', width: '100%' }}>
              <span style={{ fontSize: 14.5, fontWeight: 700, minWidth: 150, textAlign: 'start' }}>
                {isOpen ? '▾' : '▸'} {st.label}
              </span>
              <span style={{ fontSize: 19, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{fmt(st.entered)}</span>
              <span style={{ fontSize: 11.5, color: 'var(--ex-text-muted)' }}>
                נכנסו לסבב · {fmt(st.reached)} עשו מעקב אחד לפחות · ממוצע {st.avg_cm.toFixed(2)} מעקבים ·
                {' '}{fmt(st.entered - st.reached)} לא עשו אף מעקב (נספרו בשלב שמעליו)
              </span>
            </button>
            {isOpen && <CycleLevels st={st} nextLabel={labelAfter(i)} start={start} lostBy={lostBy} />}
          </div>
        );
      })}
      <div style={{ fontSize: 11.5, color: 'var(--ex-text-muted)' }}>
        הבסיס: {anchorLabel}. בכל רמה, מי שהגיע אליה התפצל לשלושה בלבד — המשיך למעקב הבא,
        עבר לפסיכיאטר, או עצר. השלושה מסתכמים למי שהגיע, והסיבות מסתכמות למי שעצר.
      </div>
    </div>
  );
}
